/**
 * Missing from price book — /rates/missing (api#1040, price-book FE-2).
 *
 * Casing ticket lines that carry no price-book link, grouped by the API so a
 * repeated description is one decision, with an "Add to price book" action
 * that creates the book item (or names an existing one) and links the lines.
 *
 * Every figure on this page is the API's, as sent: counts, the rate range, the
 * dates, which lines a relink takes. See lib/price-book-missing.ts.
 *
 * Way in: the Rate Sheets band ("Missing from book"). Roles: admin,
 * office_staff, supervisor, dispatcher — the API's own list. Anyone else meets
 * the refusal branch, which names who is allowed.
 */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BookPlus, Layers, ListChecks, Loader } from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { PageHeaderBand } from '../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../components/ui/LoadFailureNotice';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { displayValue } from '../features/fieldiq-handoff/contracts';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../lib/load-failure';
import { formatCalendarDate } from '../lib/calendar-date';
import { fmtCurrency, fmtLineItemType, fmtUnit } from '../lib/display-labels';
import { getRoleLabel } from '../lib/roles';
import { invalidateTickets } from '../lib/tickets-invalidation';
import {
  MAX_LINK_LINES,
  buildLinkBody,
  buildLinkBodyForLines,
  defaultTicked,
  parseRate,
  readDuplicateItem,
  readMissingItems,
  skipReasonLabel,
  type LinkResult,
  type MissingGroup,
} from '../lib/price-book-missing';

export const MISSING_ITEMS_QUERY_KEY = ['price-book-missing'] as const;

/** The API's two rates, as sent. One figure when they agree. */
function rateRange(g: MissingGroup): string {
  if (!Number.isFinite(g.rateMin) || !Number.isFinite(g.rateMax)) return '—';
  return g.rateMin === g.rateMax ? fmtCurrency(g.rateMin) : `${fmtCurrency(g.rateMin)} – ${fmtCurrency(g.rateMax)}`;
}

function dateRange(g: MissingGroup): string {
  const a = g.firstWorkDate ? formatCalendarDate(g.firstWorkDate) : null;
  const b = g.lastWorkDate ? formatCalendarDate(g.lastWorkDate) : null;
  if (!a && !b) return '—';
  if (a === b || !b) return a ?? '—';
  if (!a) return b;
  return `${a} – ${b}`;
}

export default function PriceBookMissingScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState<MissingGroup | null>(null);

  const query = useQuery({
    queryKey: MISSING_ITEMS_QUERY_KEY,
    queryFn: () => api.rateSheets.missingItems().then((r) => readMissingItems(r.data)),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failure = queryLoadFailure(query);
  const loading = query.isLoading || (query.fetchStatus === 'fetching' && !query.data);
  const groups = query.data?.groups ?? [];
  const counts = failure ? null : query.data?.counts ?? null;

  // A row opens its group, and so does the button inside it. The row is not a
  // button role (it holds one) and opens on Enter/Space only when it has focus
  // itself — the Rate Sheets idiom, CLAUDE.md "rows that contain inline action buttons".
  const rowKeys = (g: MissingGroup) => (event: React.KeyboardEvent<HTMLTableRowElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setOpen(g);
    }
  };

  return (
    <div data-testid="price-book-missing-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Price book"
        title="Missing from price book"
        onBack={() => navigate(-1)}
        backLabel="Back"
        stats={[
          {
            icon: <Layers className="size-4" aria-hidden="true" />,
            label: 'Groups',
            sub: 'One decision each',
            tone: 'accent',
            loading,
            value: displayValue(counts?.groups),
          },
          {
            icon: <ListChecks className="size-4" aria-hidden="true" />,
            label: 'Lines',
            sub: 'Unlinked, priced',
            tone: 'neutral',
            loading,
            value: displayValue(counts?.lines),
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <section
            aria-labelledby="missing-register-title"
            className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
          >
            <header className="border-b border-fieldiq-border px-4 py-4">
              <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Unlinked lines</p>
              <h2 id="missing-register-title" className="mt-1 text-base font-extrabold tracking-tight text-white">
                Ticket lines with no price-book item
              </h2>
            </header>

            {loading ? (
              <div className="space-y-3 p-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-12 rounded-fi-panel bg-fieldiq-dark/60 animate-pulse" />
                ))}
              </div>
            ) : failure ? (
              <LoadFailureNotice
                error={queryFailureError(query)}
                kind={failure}
                subject="ticket lines missing from the price book"
                roleLabel={user?.role ? getRoleLabel(user.role) : undefined}
                allowed="admins, office staff, supervisors and dispatchers"
                className="p-6"
              />
            ) : groups.length === 0 ? (
              <p className="px-4 py-12 text-center text-sm text-fieldiq-muted">
                Every priced line on a live casing ticket is linked to the price book or marked off book.
              </p>
            ) : (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[1040px] text-left">
                    <thead className="text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                      <tr className="border-l-4 border-l-transparent">
                        <th className="px-4 py-3">Description</th>
                        <th className="px-4 py-3">Type · Unit</th>
                        <th className="px-4 py-3 text-right">Lines</th>
                        <th className="px-4 py-3 text-right">Invoiced</th>
                        <th className="px-4 py-3">Ticket rates</th>
                        <th className="px-4 py-3">Work dates</th>
                        <th className="px-4 py-3">Already in the book</th>
                        <th className="px-4 py-3"><span className="sr-only">Action</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      {groups.map((g) => (
                        <tr
                          key={`${g.key}|${g.lineItemType}|${g.unit}`}
                          data-testid="missing-row"
                          tabIndex={0}
                          onClick={() => setOpen(g)}
                          onKeyDown={rowKeys(g)}
                          // No status on a group, so the rail keeps its width and claims no colour.
                          className="cursor-pointer border-t border-l-4 border-fieldiq-border border-l-transparent outline-none transition-colors hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10"
                        >
                          <td className="px-4 py-3">
                            <span className="text-sm font-extrabold text-white">{g.description || '—'}</span>
                            {g.descriptions.length > 1 && (
                              <span className="block text-xs text-fieldiq-muted">
                                {g.descriptions.length} spellings
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-fieldiq-text">
                            {fmtLineItemType(g.lineItemType)} · {fmtUnit(g.unit) || '—'}
                          </td>
                          <td className="px-4 py-3 text-right text-sm tabular-nums text-fieldiq-text">
                            {displayValue(g.lineCount)}
                            <span className="block text-xs text-fieldiq-muted">
                              {displayValue(g.ticketCount)} {g.ticketCount === 1 ? 'ticket' : 'tickets'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right text-sm tabular-nums text-fieldiq-text">
                            {displayValue(g.invoicedLineCount)}
                          </td>
                          <td className="px-4 py-3 text-sm tabular-nums text-fieldiq-text">{rateRange(g)}</td>
                          <td className="px-4 py-3 text-sm text-fieldiq-text">{dateRange(g)}</td>
                          <td className="px-4 py-3 text-sm text-fieldiq-text">
                            {g.bookMatches.length === 0
                              ? <span className="text-fieldiq-muted">No</span>
                              : g.bookMatches.length === 1
                                ? g.bookMatches[0].rateSheetName
                                : `${g.bookMatches.length} items`}
                          </td>
                          <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              onClick={() => setOpen(g)}
                              className="fi-action fi-action--standard whitespace-nowrap bg-fieldiq-card border border-fieldiq-border"
                            >
                              <BookPlus className="size-4" aria-hidden="true" />
                              Add to price book
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="space-y-3 p-4 md:hidden">
                  {groups.map((g) => (
                    <div key={`${g.key}|${g.lineItemType}|${g.unit}`} className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-4">
                      <p className="font-extrabold text-white">{g.description || '—'}</p>
                      <p className="mt-1 text-xs text-fieldiq-muted">
                        {fmtLineItemType(g.lineItemType)} · {fmtUnit(g.unit) || '—'} · {rateRange(g)}
                      </p>
                      <p className="mt-1 text-xs text-fieldiq-muted">
                        {displayValue(g.lineCount)} lines on {displayValue(g.ticketCount)} tickets ·{' '}
                        {displayValue(g.invoicedLineCount)} invoiced
                      </p>
                      <button
                        type="button"
                        onClick={() => setOpen(g)}
                        className="fi-action fi-action--standard mt-3 bg-fieldiq-card border border-fieldiq-border"
                      >
                        <BookPlus className="size-4" aria-hidden="true" />
                        Add to price book
                      </button>
                    </div>
                  ))}
                </div>

                <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">
                  {displayValue(counts?.groups)} groups · {displayValue(counts?.lines)} lines, of which{' '}
                  {displayValue(counts?.relinkableLines)} are on tickets not yet invoiced.
                </p>
              </>
            )}
          </section>
        </div>
      </section>

      {open && <AddToBookDialog group={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

interface SheetOption {
  id: string;
  name: string;
}

function AddToBookDialog({ group, onClose }: { group: MissingGroup; onClose: () => void }) {
  const queryClient = useQueryClient();
  // An existing item of the same name is offered first: linking to it beats a duplicate.
  const [mode, setMode] = useState<'existing' | 'new'>(group.bookMatches.length > 0 ? 'existing' : 'new');
  const [existingId, setExistingId] = useState(group.bookMatches[0]?.rateSheetItemId ?? '');
  const [sheetId, setSheetId] = useState('');
  const [name, setName] = useState(group.description);
  const [unit, setUnit] = useState(group.unit);
  // Deliberately blank: never prefilled from the lines (see parseRate).
  const [rate, setRate] = useState('');
  const [includeInvoiced, setIncludeInvoiced] = useState(false);
  // Per-line ticks when the API sent `lines` (api#1044); null means it did not,
  // and the group-level choice above is the one in force.
  const perLine = Array.isArray(group.lines) ? group.lines : null;
  const [ticked, setTicked] = useState<Set<string>>(() => (perLine ? defaultTicked(perLine) : new Set()));
  const toggle = (id: string) =>
    setTicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  const [refusal, setRefusal] = useState<string | null>(null);
  const [duplicateOf, setDuplicateOf] = useState<string | null>(null);
  const [result, setResult] = useState<LinkResult | null>(null);

  const sheetsQuery = useQuery({
    queryKey: ['rate-sheets'],
    queryFn: () => api.rateSheets.list().then((r) => r.data.data ?? []),
    enabled: mode === 'new',
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  // Active sheets only: an item on an inactive sheet is in no picker and
  // satisfies no approval gate, so it would link the lines to nothing usable.
  const sheets: SheetOption[] = useMemo(
    () =>
      (Array.isArray(sheetsQuery.data) ? sheetsQuery.data : [])
        .filter((s: any) => s.status === 'active')
        .map((s: any) => ({ id: String(s.id), name: String(s.name ?? '—') })),
    [sheetsQuery.data]
  );

  const price = parseRate(rate);
  // How many lines the button will send: the office's own ticks, or — without
  // per-line detail — whichever of the API's two counts the checkbox selects.
  const willTake = perLine ? ticked.size : includeInvoiced ? group.lineCount : group.relinkableLineCount;
  const formReady =
    willTake > 0 &&
    willTake <= MAX_LINK_LINES &&
    (mode === 'existing'
      ? !!existingId
      : !!sheetId && name.trim() !== '' && unit.trim() !== '' && price !== null);

  const link = useMutation({
    mutationFn: (target: { rateSheetItemId: string } | { item: { rateSheetId: string; serviceName: string; unit: string; price: number } }) =>
      api.rateSheets.linkMissingItems(
        perLine ? buildLinkBodyForLines(perLine, ticked, target) : buildLinkBody(group, target, includeInvoiced)
      ),
    onMutate: () => {
      setRefusal(null);
      setDuplicateOf(null);
    },
    onSuccess: (res) => {
      const data: LinkResult | null = res.data?.data ?? null;
      setResult(data);
      queryClient.invalidateQueries({ queryKey: MISSING_ITEMS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['rate-sheets'] });
      invalidateTickets(queryClient);
    },
    onError: (err: any) => {
      const dup = readDuplicateItem(err);
      if (dup) {
        setRefusal(dup.message);
        setDuplicateOf(dup.existingItemId);
        return;
      }
      setRefusal(err?.response?.data?.error ?? 'The lines could not be linked. Nothing was changed.');
    },
  });

  const submit = () => {
    if (!formReady) return;
    if (mode === 'existing') link.mutate({ rateSheetItemId: existingId });
    else link.mutate({ item: { rateSheetId: sheetId, serviceName: name.trim(), unit: unit.trim(), price: price as number } });
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Add to price book</DialogTitle>
          <p className="mt-1 text-sm text-fieldiq-muted">
            {group.description || '—'} · {fmtLineItemType(group.lineItemType)} · {fmtUnit(group.unit) || '—'} · ticket
            rates {rateRange(group)}
          </p>
        </DialogHeader>

        {result ? (
          <div data-testid="link-receipt" className="space-y-3 text-sm">
            <p className="text-white">
              {result.item.created ? 'Added' : 'Used'} <strong>{result.item.serviceName}</strong> at{' '}
              {fmtCurrency(result.item.price)} {fmtUnit(result.item.unit)}. Linked {displayValue(result.counts.linked)}{' '}
              {result.counts.linked === 1 ? 'line' : 'lines'}; skipped {displayValue(result.counts.skipped)}.
            </p>
            {result.linked.length > 0 && (
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-fieldiq-muted">Linked</p>
                <ul className="mt-1 text-fieldiq-text">
                  {result.linked.map((l) => (
                    <li key={l.lineItemId}>{l.ticketNumber ?? '—'}</li>
                  ))}
                </ul>
              </div>
            )}
            {result.skipped.length > 0 && (
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-fieldiq-muted">Skipped</p>
                <ul className="mt-1 text-fieldiq-text">
                  {result.skipped.map((l) => (
                    <li key={l.lineItemId}>
                      {l.ticketNumber ?? '—'} — {skipReasonLabel(l.reason)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <DialogFooter>
              <button type="button" onClick={onClose} className="fi-action fi-action--standard fi-action--primary">
                Done
              </button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4 text-sm">
            {group.bookMatches.length > 0 && (
              <fieldset className="space-y-2">
                <legend className="text-xs font-bold uppercase tracking-wider text-fieldiq-muted">Item</legend>
                <label className="flex items-start gap-2">
                  <input type="radio" name="mode" checked={mode === 'existing'} onChange={() => setMode('existing')} className="mt-1 accent-fi-amber" />
                  <span className="text-fieldiq-text">Link to an item already in the book</span>
                </label>
                {mode === 'existing' && (
                  <select
                    aria-label="Existing item"
                    value={existingId}
                    onChange={(e) => setExistingId(e.target.value)}
                    className="w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 py-2 text-fieldiq-text"
                  >
                    {group.bookMatches.map((m) => (
                      <option key={m.rateSheetItemId} value={m.rateSheetItemId}>
                        {m.serviceName} — {m.rateSheetName} — {fmtCurrency(m.price)} {fmtUnit(m.unit)}
                      </option>
                    ))}
                  </select>
                )}
                <label className="flex items-start gap-2">
                  <input type="radio" name="mode" checked={mode === 'new'} onChange={() => setMode('new')} className="mt-1 accent-fi-amber" />
                  <span className="text-fieldiq-text">Add a new item</span>
                </label>
              </fieldset>
            )}

            {mode === 'new' && (
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="sm:col-span-2">
                  <span className="text-xs text-fieldiq-muted">Rate sheet</span>
                  {sheetsQuery.isLoading ? (
                    <p className="mt-1 flex items-center gap-1 text-fieldiq-muted"><Loader className="size-3 animate-spin" /> Loading sheets…</p>
                  ) : queryLoadFailure(sheetsQuery) ? (
                    <LoadFailureNotice error={queryFailureError(sheetsQuery)} kind={queryLoadFailure(sheetsQuery)!} subject="rate sheets" />
                  ) : sheets.length === 0 ? (
                    <p className="mt-1 text-fieldiq-muted">There is no active rate sheet to add the item to.</p>
                  ) : (
                    <select
                      aria-label="Rate sheet"
                      value={sheetId}
                      onChange={(e) => setSheetId(e.target.value)}
                      className="mt-1 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 py-2 text-fieldiq-text"
                    >
                      <option value="">Pick a sheet…</option>
                      {sheets.map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  )}
                </label>
                <label className="sm:col-span-2">
                  <span className="text-xs text-fieldiq-muted">Name</span>
                  <input
                    aria-label="Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    maxLength={255}
                    className="mt-1 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 py-2 text-fieldiq-text"
                  />
                </label>
                <label>
                  <span className="text-xs text-fieldiq-muted">Unit</span>
                  <input
                    aria-label="Unit"
                    value={unit}
                    onChange={(e) => setUnit(e.target.value)}
                    maxLength={50}
                    className="mt-1 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 py-2 text-fieldiq-text"
                  />
                </label>
                <label>
                  <span className="text-xs text-fieldiq-muted">Book rate</span>
                  <input
                    aria-label="Book rate"
                    inputMode="decimal"
                    value={rate}
                    onChange={(e) => setRate(e.target.value)}
                    placeholder="Required"
                    className="mt-1 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 py-2 text-fieldiq-text"
                  />
                </label>
                <p className="text-xs text-fieldiq-muted sm:col-span-2">
                  The book rate is yours to set. Linking changes no ticket or invoice amount.
                </p>
              </div>
            )}

            {perLine ? (
              <fieldset className="rounded-fi-panel border border-fieldiq-border p-3">
                <legend className="px-1 text-xs font-bold uppercase tracking-wider text-fieldiq-muted">Lines to link</legend>
                <ul className="max-h-64 space-y-1 overflow-y-auto">
                  {perLine.map((l) => (
                    <li key={l.lineItemId}>
                      <label className="flex items-start gap-2 rounded px-1 py-1 hover:bg-fieldiq-dark/50">
                        <input
                          type="checkbox"
                          checked={ticked.has(l.lineItemId)}
                          onChange={() => toggle(l.lineItemId)}
                          aria-label={`${l.ticketNumber ?? 'Ticket'} — ${l.description}${l.invoiced ? ' (invoiced)' : ''}`}
                          className="mt-1 accent-fi-amber"
                        />
                        <span className="min-w-0 flex-1">
                          <span className="flex flex-wrap items-center gap-x-2 text-fieldiq-text">
                            <span className="font-semibold">{l.ticketNumber ?? '—'}</span>
                            <span className="text-fieldiq-muted">{l.workDate ? formatCalendarDate(l.workDate) : '—'}</span>
                            {l.invoiced && (
                              <span className="rounded border border-black/20 bg-fi-signal-amber px-1.5 text-[10px] font-bold uppercase text-fi-signal-ink">
                                Invoiced
                              </span>
                            )}
                          </span>
                          <span className="block truncate text-xs text-fieldiq-muted">
                            {l.description || '—'} · {displayValue(l.quantity)} {fmtUnit(l.unit)} @ {fmtCurrency(l.rate)} = {fmtCurrency(l.total)}
                          </span>
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
                {group.invoicedLineCount > 0 && (
                  <p className="mt-2 text-xs text-fieldiq-muted">
                    Invoiced lines start unticked. Linking one changes no amount, but its next QuickBooks push will use this item.
                  </p>
                )}
                {willTake > MAX_LINK_LINES && (
                  <p className="mt-2 text-xs text-fieldiq-text">One link takes at most {MAX_LINK_LINES} lines — untick some and link the rest after.</p>
                )}
              </fieldset>
            ) : (
              <fieldset className="space-y-2 rounded-fi-panel border border-fieldiq-border p-3">
                <legend className="px-1 text-xs font-bold uppercase tracking-wider text-fieldiq-muted">Lines to link</legend>
                <p className="text-fieldiq-text">
                  {displayValue(group.relinkableLineCount)} {group.relinkableLineCount === 1 ? 'line' : 'lines'} on tickets not yet
                  invoiced.
                </p>
                {group.invoicedLineCount > 0 && (
                  <label className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      checked={includeInvoiced}
                      onChange={(e) => setIncludeInvoiced(e.target.checked)}
                      className="mt-1 accent-fi-amber"
                    />
                    <span className="text-fieldiq-text">
                      Also link the {displayValue(group.invoicedLineCount)} {group.invoicedLineCount === 1 ? 'line' : 'lines'} on
                      invoiced tickets
                      <span className="block text-xs text-fieldiq-muted">
                        No amount changes, but their next QuickBooks push will use this item.
                      </span>
                    </span>
                  </label>
                )}
                {willTake === 0 && (
                  <p className="text-xs text-fieldiq-muted">Every line in this group is on an invoiced ticket. Tick the box to link them.</p>
                )}
              </fieldset>
            )}

            {refusal && (
              <div role="alert" className="rounded-fi-panel border border-fieldiq-danger/40 bg-fieldiq-danger/10 p-3 text-fieldiq-text">
                {refusal}
                {duplicateOf && (
                  <button
                    type="button"
                    onClick={() => link.mutate({ rateSheetItemId: duplicateOf })}
                    className="fi-action fi-action--standard mt-2 bg-fieldiq-card border border-fieldiq-border"
                  >
                    Link to it instead
                  </button>
                )}
              </div>
            )}

            <DialogFooter>
              <button type="button" onClick={onClose} className="fi-action fi-action--standard bg-fieldiq-card border border-fieldiq-border">
                Cancel
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={!formReady || link.isPending}
                className="fi-action fi-action--standard fi-action--primary disabled:opacity-50"
              >
                {link.isPending ? <Loader className="size-4 animate-spin" aria-hidden="true" /> : <BookPlus className="size-4" aria-hidden="true" />}
                {mode === 'existing' ? 'Link' : 'Add and link'} {displayValue(willTake)} {willTake === 1 ? 'line' : 'lines'}
              </button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
